import type { Identifier } from "ra-core";

import type { ComisionFila, MiComision, PapelComision } from "./tipos";

/**
 * Comisiones (S9, Jan y Robert 06-07/10/2026; 40, Robert 23/10/2026: una por persona y cobro). La
 * base de datos calcula la comisión de cada persona por cada cobro (tabla comisiones: setter 5 %,
 * consultor 10 %, sobre la base sin IVA); aquí solo se lee y se suma para enseñarla. Pendiente de
 * decidir: qué pasa con las devoluciones, por eso la app enseña la comisión generada por cobro y no
 * hace liquidaciones.
 */

type Traductor = (clave: string, opciones?: Record<string, unknown>) => string;

/** Euros con céntimos (las comisiones se pagan al céntimo). */
export const euros = (n: number) =>
  new Intl.NumberFormat("es-ES", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);

const porcentaje = (n: number) =>
  new Intl.NumberFormat("es-ES", { maximumFractionDigits: 2 }).format(n);

/** Una línea de texto con la comisión de una persona por un cobro, o por qué no la lleva. */
export const textoComision = (
  k: Partial<
    Pick<ComisionFila, "sale_id" | "pct" | "importe" | "motivo" | "papel">
  >,
  translate: Traductor,
  nombre: (id?: Identifier | null) => string,
): string => {
  if (k.sale_id != null && !k.motivo && k.importe != null) {
    const persona = nombre(k.sale_id) || "—";
    return translate("nevora.comisiones.del_cobro", {
      importe: euros(Number(k.importe)),
      pct: porcentaje(Number(k.pct ?? 0)),
      persona: k.papel
        ? `${persona} (${translate(`nevora.comisiones.papeles.${k.papel}`)})`
        : persona,
    });
  }
  return translate(`nevora.comisiones.motivos.${k.motivo ?? "sin_comercial"}`);
};

export type ResumenComisionPersona = {
  para: Identifier;
  papel: PapelComision;
  cobros: number;
  /** Suma de la base imponible (sin IVA) de sus cobros con comisión. */
  base: number;
  comision: number;
};

/** Suma por persona y papel de las comisiones del periodo, de mayor a menor. */
export const comisionesPorPersona = (
  filas: Pick<ComisionFila, "sale_id" | "papel" | "importe" | "base">[],
): ResumenComisionPersona[] => {
  const mapa = new Map<string, ResumenComisionPersona>();
  for (const k of filas) {
    if (k.sale_id == null) continue;
    const clave = `${k.sale_id}|${k.papel}`;
    const fila = mapa.get(clave) ?? {
      para: k.sale_id,
      papel: k.papel,
      cobros: 0,
      base: 0,
      comision: 0,
    };
    fila.cobros += 1;
    fila.base += Number(k.base);
    fila.comision += Number(k.importe);
    mapa.set(clave, fila);
  }
  const redondeo = (n: number) => Math.round(n * 100) / 100;
  return [...mapa.values()]
    .map((f) => ({
      ...f,
      base: redondeo(f.base),
      comision: redondeo(f.comision),
    }))
    .sort((a, b) => b.comision - a.comision);
};

/** «aaaa-mm» del mes de una fecha. */
export const mesDe = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;

/** Primer día del mes y primer día del siguiente («aaaa-mm-dd»), para filtrar cobros. */
export const diasMes = (mes: string): { desde: string; hasta: string } => {
  const [a, m] = mes.split("-").map(Number);
  const siguiente =
    m === 12 ? `${a + 1}-01` : `${a}-${String(m + 1).padStart(2, "0")}`;
  return { desde: `${mes}-01`, hasta: `${siguiente}-01` };
};

/** 36: una venta en «Mis comisiones», con sus cobros (40: también la del consultor, con su papel). */
export type VentaDelSetter = {
  proyecto_id: Identifier;
  negocio_id: Identifier;
  negocio: string;
  etapa: MiComision["etapa"];
  papel: PapelComision | null;
  aceptada_at: string | null;
  base_firmada: number | null;
  comision_estimada: number | null;
  cobros: {
    cobro_id: Identifier;
    cobrado_el: string | null;
    importe: number;
    comision: number | null;
    comision_pagada_at: string | null;
  }[];
};

/**
 * 36: agrupa las filas de nevora_mis_comisiones (una por cobro, o una por venta sin cobros) en
 * ventas con sus cobros, en el orden en que llegan (las aceptadas más recientes primero), y suma
 * la comisión estimada, la de lo cobrado y la ya pagada. 40: con `mes` («aaaa-mm»), solo los cobros
 * de ese mes, y la estimada de las ventas aceptadas ese mes.
 */
export const agruparMisComisiones = (
  filas: MiComision[],
  mes?: string,
): {
  ventas: VentaDelSetter[];
  totalEstimada: number;
  totalCobrada: number;
  totalPagada: number;
} => {
  const aceptadaEnMes = (f: Pick<MiComision, "aceptada_at">) =>
    !mes || (!!f.aceptada_at && mesDe(new Date(f.aceptada_at)) === mes);
  const delMes = (f: MiComision) =>
    !mes ||
    (f.cobro_id != null
      ? (f.cobrado_el ?? "").startsWith(mes)
      : aceptadaEnMes(f));
  const ventas: VentaDelSetter[] = [];
  const porProyecto = new Map<string, VentaDelSetter>();
  for (const f of filas.filter(delMes)) {
    const clave = `${f.proyecto_id}|${f.papel ?? ""}`;
    let v = porProyecto.get(clave);
    if (!v) {
      v = {
        proyecto_id: f.proyecto_id,
        negocio_id: f.negocio_id,
        negocio: f.negocio,
        etapa: f.etapa,
        papel: f.papel ?? null,
        aceptada_at: f.aceptada_at,
        base_firmada: f.base_firmada == null ? null : Number(f.base_firmada),
        comision_estimada:
          f.comision_estimada == null ? null : Number(f.comision_estimada),
        cobros: [],
      };
      porProyecto.set(clave, v);
      ventas.push(v);
    }
    if (f.cobro_id != null) {
      v.cobros.push({
        cobro_id: f.cobro_id,
        cobrado_el: f.cobrado_el,
        importe: Number(f.importe ?? 0),
        comision: f.comision == null ? null : Number(f.comision),
        comision_pagada_at: f.comision_pagada_at,
      });
    }
  }
  const redondear = (n: number) => Math.round(n * 100) / 100;
  const cobros = ventas.flatMap((v) => v.cobros);
  return {
    ventas,
    totalEstimada: redondear(
      ventas
        .filter(aceptadaEnMes)
        .reduce((s, v) => s + (v.comision_estimada ?? 0), 0),
    ),
    totalCobrada: redondear(cobros.reduce((s, c) => s + (c.comision ?? 0), 0)),
    totalPagada: redondear(
      cobros
        .filter((c) => c.comision_pagada_at)
        .reduce((s, c) => s + (c.comision ?? 0), 0),
    ),
  };
};
